/**
 * YieldPredictor.js - The "Oracle" AI Engine
 * 
 * Purpose: Statistical Regression Models to forecast yield outcomes.
 * 
 * Logic Tier: Level 2 (Statistical AI)
 */

export const yieldPredictor = {
    /**
     * Calculate projected yield based on inputs
     * @param {string} crop - 'Corn', 'Soybeans', 'Wheat'
     * @param {Object} sensors - Current sensor averages
     * @param {Array} history - Array of previous years' yields (optional)
     */
    predictYield(crop, sensors, history = []) {
        console.log("Predicting for", crop, sensors); // Debug log

        let baseYield = 0;
        let unit = '';

        // Baselines (DMV Region Averages)
        switch (crop) {
            case 'Corn': baseYield = 170; unit = 'bu/acre'; break;
            case 'Soybeans': baseYield = 55; unit = 'bu/acre'; break;
            case 'Wheat': baseYield = 80; unit = 'bu/acre'; break;
            default: baseYield = 100;
        }

        // 0. Historical Factor (Trend)
        let histFactor = 1.0;
        let histTrend = 0;
        if (history && history.length > 0) {
            const avgHist = history.reduce((a, b) => a + b, 0) / history.length;
            // If historical avg is higher than baseline, farm is efficient -> bonus
            histTrend = (avgHist - baseYield) / baseYield;
            // Apply half of the trend as a correction (conservative model)
            histFactor = 1 + (histTrend * 0.5);
        }

        // Multipliers (The "AI" Weighting)

        // 1. Nitrogen Factor (Corn is sensitive)
        let nFactor = 1.0;
        if (crop === 'Corn') {
            // Corn needs N > 40. Linear dropoff below that.
            nFactor = Math.min(1.2, Math.max(0.6, sensors.nitrogen / 40));
        }

        // 2. Moisture Factor (Goldilocks zone: 50-70%)
        let mFactor = 1.0;
        if (sensors.moisture < 45) mFactor = 0.7; // Drought
        else if (sensors.moisture > 85) mFactor = 0.8; // Saturation
        else if (sensors.moisture >= 55 && sensors.moisture <= 75) mFactor = 1.1; // Perfect

        // 3. pH Factor (Acidic penalty)
        let phFactor = 1.0;
        if (sensors.ph < 5.8 || sensors.ph > 7.5) phFactor = 0.9;

        // Calculate
        const finalYield = Math.round(baseYield * histFactor * nFactor * mFactor * phFactor);

        // detailed factors for UI
        const factors = [
            { name: 'Base Regional Yield', value: baseYield, impact: 'neutral' },
            { name: 'Historical Perf.', value: `${(histTrend * 100).toFixed(1)}%`, impact: histTrend >= 0 ? 'positive' : 'negative' },
            { name: 'Nitrogen Availability', value: `${((nFactor - 1) * 100).toFixed(1)}%`, impact: nFactor >= 1 ? 'positive' : 'negative' },
            { name: 'Soil Moisture', value: `${((mFactor - 1) * 100).toFixed(1)}%`, impact: mFactor >= 1 ? 'positive' : 'negative' },
            { name: 'pH Balance', value: `${((phFactor - 1) * 100).toFixed(1)}%`, impact: phFactor >= 1 ? 'positive' : 'negative' }
        ];

        return {
            value: finalYield,
            unit: unit,
            confidence: 85 + (history.length * 2), // More history = higher confidence
            factors: factors
        };
    }
};
